import React, { useEffect, useState, useCallback } from 'react'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Label } from '@/components/ui/label'
import { adminClient, type LeadHistory } from '@/api/adminClient'
import { ChevronDown, ChevronUp, ChevronLeft, ChevronRight } from 'lucide-react'

interface PaginationInfo {
  page: number
  limit: number
  total: number
  totalPages: number
  hasNext: boolean
  hasPrev: boolean
}

interface RaynetCategory {
  id: number
  code01: string
  code02: string | null
  strValue01: string | null
}

interface RaynetSource {
  id: number
  code01: string
  code02: string | null
  strValue01: string | null
}

const MonitoringTab = () => {
  const [leadHistory, setLeadHistory] = useState<LeadHistory[]>([])
  const [pagination, setPagination] = useState<PaginationInfo>({
    page: 1,
    limit: 10,
    total: 0,
    totalPages: 0,
    hasNext: false,
    hasPrev: false
  })
  const [expandedLeadId, setExpandedLeadId] = useState<number | null>(null)
  const [raynetStatus, setRaynetStatus] = useState<'checking' | 'ok' | 'error'>('checking')
  const [loading, setLoading] = useState(false)
  const [raynetCategories, setRaynetCategories] = useState<RaynetCategory[]>([])
  const [raynetSources, setRaynetSources] = useState<RaynetSource[]>([])
  const [raynetDataLoaded, setRaynetDataLoaded] = useState(false)

  // Fallback toast function
  const toast = useCallback(({ title, description }: { title: string; description?: string }) => {
    alert(`${title}${description ? '\n' + description : ''}`)
  }, [])

  // Načtení kategorií z Raynet API
  const loadRaynetCategories = useCallback(async () => {
    try {
      const instance = import.meta.env.VITE_RAYNET_INSTANCE
      const user = import.meta.env.VITE_RAYNET_USER
      const apiKey = import.meta.env.VITE_RAYNET_API_KEY

      if (!instance || !user || !apiKey) {
        console.warn('Raynet konfigurace není kompletní pro načítání kategorií')
        return
      }

      const authString = btoa(`${user}:${apiKey}`)
      const response = await fetch('https://app.raynet.cz/api/v2/leadCategory', {
        headers: {
          'X-Instance-Name': instance,
          'Authorization': `Basic ${authString}`,
        },
      })

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`)
      }

      const data = await response.json()
      if (data.success && data.data) {
        setRaynetCategories(data.data)
      }
    } catch (error) {
      console.error('Chyba při načítání kategorií z Raynet:', error)
    }
  }, [])

  // Načtení zdrojů z Raynet API
  const loadRaynetSources = useCallback(async () => {
    try {
      const instance = import.meta.env.VITE_RAYNET_INSTANCE
      const user = import.meta.env.VITE_RAYNET_USER
      const apiKey = import.meta.env.VITE_RAYNET_API_KEY

      if (!instance || !user || !apiKey) {
        console.warn('Raynet konfigurace není kompletní pro načítání zdrojů')
        return
      }

      const authString = btoa(`${user}:${apiKey}`)
      const response = await fetch('https://app.raynet.cz/api/v2/contactSource', {
        headers: {
          'X-Instance-Name': instance,
          'Authorization': `Basic ${authString}`,
        },
      })

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`)
      }

      const data = await response.json()
      if (data.success && data.data) {
        setRaynetSources(data.data)
      }
    } catch (error) {
      console.error('Chyba při načítání zdrojů z Raynet:', error)
    }
  }, [])

  // Helper funkce pro získání názvu kategorie podle ID
  const getCategoryName = useCallback((categoryId: number): string => {
    const category = raynetCategories.find(c => c.id === categoryId)
    return category ? category.code01 || `Kategorie ${categoryId}` : `Neznámá kategorie (ID: ${categoryId})`
  }, [raynetCategories])

  // Helper funkce pro získání názvu zdroje podle ID
  const getSourceName = useCallback((sourceId: number): string => {
    const source = raynetSources.find(s => s.id === sourceId)
    return source ? source.code01 || `Zdroj ${sourceId}` : `Neznámý zdroj (ID: ${sourceId})`
  }, [raynetSources])

  const formatReadableData = useCallback((lead: LeadHistory) => {
    const leadData = (lead.lead_data as Record<string, unknown>) || {}
    
    // Získání sourceId a categoryId z lead_data
    const sourceId = Number(leadData.sourceId) || 0
    const categoryId = Number(leadData.categoryId) || 0
    
    return {
      'Raynet ID': lead.raynet_id || 'Nepřiřazeno',
      'Status': lead.status === 'success' ? 'Úspěch' : 'Chyba',
      'Jméno': String(leadData.name || '') || `${String(leadData.firstName || '')} ${String(leadData.lastName || '')}`.trim() || 'Neuvedeno',
      'Email': String(leadData.email || '') || 'Neuvedeno',
      'Telefon': String(leadData.phone || '') || 'Neuvedeno',
      'Společnost': String(leadData.company || '') || 'Neuvedeno',
      'Poznámka': String(leadData.note || '') || 'Neuvedeno',
      'Téma': String(leadData.topic || '') || 'Neuvedeno',
      'Kategorie leadu': categoryId ? getCategoryName(categoryId) : 'Neuvedeno',
      'Zdroj leadu': sourceId ? getSourceName(sourceId) : 'Neuvedeno',
      'Datum vytvoření': new Date(lead.created_at).toLocaleString('cs-CZ'),
      'Chybová zpráva': lead.error_message || 'Žádná'
    }
  }, [getCategoryName, getSourceName])

  const loadLeadHistory = useCallback(async (page: number = 1, limit: number = 10) => {
    setLoading(true)
    try {
      // Použijeme adminClient metodu s pagination parametry
      const data = await adminClient.getLeads(page, limit)
      setLeadHistory(data.data)
      setPagination(data.pagination)
    } catch (error) {
      console.error('Chyba při načítání historie leadů:', error)
      toast({
        title: 'Chyba',
        description: 'Nepodařilo se načíst historii leadů'
      })
    } finally {
      setLoading(false)
    }
  }, [toast])

  const checkRaynetStatus = useCallback(async () => {
    try {
      await adminClient.getRaynetStatus()
      setRaynetStatus('ok')
    } catch {
      setRaynetStatus('error')
    }
  }, [])

  useEffect(() => {
    const loadData = async () => {
      await Promise.all([
        loadLeadHistory(),
        checkRaynetStatus()
      ])
    }
    loadData()
  }, [loadLeadHistory, checkRaynetStatus])

  // Načtení Raynet dat při prvním zobrazení
  useEffect(() => {
    if (!raynetDataLoaded) {
      const loadRaynetData = async () => {
        await Promise.all([
          loadRaynetCategories(),
          loadRaynetSources()
        ])
        setRaynetDataLoaded(true)
      }
      loadRaynetData()
    }
  }, [raynetDataLoaded, loadRaynetCategories, loadRaynetSources])

  const handlePageChange = (newPage: number) => {
    loadLeadHistory(newPage, pagination.limit)
  }

  const handleLimitChange = (newLimit: number) => {
    loadLeadHistory(1, newLimit)
  }

  const toggleExpanded = (leadId: number) => {
    setExpandedLeadId(prev => prev === leadId ? null : leadId)
  }

  return (
    <div className="space-y-6">
      {/* Status Raynet API */}
      <Card>
        <CardHeader>
          <CardTitle>Stav Raynet API</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="flex items-center space-x-2">
            <div 
              className={`w-3 h-3 rounded-full ${
                raynetStatus === 'checking' ? 'bg-yellow-500' :
                raynetStatus === 'ok' ? 'bg-green-500' : 'bg-red-500'
              }`}
            />
            <span>
              {raynetStatus === 'checking' ? 'Ověřuji připojení...' :
               raynetStatus === 'ok' ? 'Připojeno' : 'Nedostupné'}
            </span>
            <Button 
              variant="outline" 
              size="sm" 
              onClick={checkRaynetStatus}
              disabled={raynetStatus === 'checking'}
            >
              Obnovit
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Historie leadů */}
      <Card>
        <CardHeader>
          <CardTitle>Historie leadů</CardTitle>
          <CardDescription>
            Celkem {pagination.total} záznamů
            {!raynetDataLoaded && (
              <span className="text-yellow-600 ml-2">
                (Načítám kategorie a zdroje z Raynet...)
              </span>
            )}
          </CardDescription>
        </CardHeader>
        <CardContent>
          {/* Ovládání stránkování a velikosti stránek */}
          <div className="flex justify-between items-center mb-4">
            <div className="flex items-center space-x-2">
              <Label htmlFor="pageSize">Záznamů na stránku:</Label>
              <select
                id="pageSize"
                value={pagination.limit}
                onChange={(e) => handleLimitChange(Number(e.target.value))}
                className="border rounded px-2 py-1"
              >
                <option value={5}>5</option>
                <option value={10}>10</option>
                <option value={20}>20</option>
                <option value={50}>50</option>
              </select>
            </div>

            <div className="flex items-center space-x-2">
              <Button
                variant="outline"
                size="sm"
                onClick={() => handlePageChange(pagination.page - 1)}
                disabled={!pagination.hasPrev || loading}
              >
                <ChevronLeft className="h-4 w-4" />
                Předchozí
              </Button>

              <span className="text-sm">
                Stránka {pagination.page} z {pagination.totalPages}
              </span>

              <Button
                variant="outline"
                size="sm"
                onClick={() => handlePageChange(pagination.page + 1)}
                disabled={!pagination.hasNext || loading}
              >
                Další
                <ChevronRight className="h-4 w-4" />
              </Button>
            </div>
          </div>

          {loading ? (
            <div className="text-center py-4">
              Načítám historii leadů...
            </div>
          ) : leadHistory.length === 0 ? (
            <div className="text-center py-4 text-gray-500">
              Žádné leady k zobrazení
            </div>
          ) : (
            <div className="space-y-4">
              {leadHistory.map((lead) => (
                <div key={lead.id} className="border rounded p-4">
                  <div className="flex justify-between items-start">
                    <div>
                      <h3 className="font-semibold">
                        {(() => {
                          const leadData = (lead.lead_data as Record<string, unknown>) || {}
                          return String(leadData.name || '') || `${String(leadData.firstName || '')} ${String(leadData.lastName || '')}`.trim() || 'Neuvedené jméno'
                        })()}
                      </h3>
                      <p className="text-sm text-gray-600">
                        {(() => {
                          const leadData = (lead.lead_data as Record<string, unknown>) || {}
                          return String(leadData.email || '') || 'Neuvedený email'
                        })()}
                      </p>
                      <p className="text-sm text-gray-500">
                        {new Date(lead.created_at).toLocaleString('cs-CZ')} - {lead.status === 'success' ? '✅ Úspěch' : '❌ Chyba'}
                      </p>
                    </div>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => toggleExpanded(lead.id)}
                    >
                      {expandedLeadId === lead.id ? (
                        <>
                          Skrýt <ChevronUp className="ml-1 h-4 w-4" />
                        </>
                      ) : (
                        <>
                          Detail <ChevronDown className="ml-1 h-4 w-4" />
                        </>
                      )}
                    </Button>
                  </div>

                  {expandedLeadId === lead.id && (
                    <div className="mt-4 pt-4 border-t">
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        {/* Lidsky čitelný formát */}
                        <div>
                          <h4 className="font-medium mb-2">Údaje</h4>
                          <div className="bg-gray-50 p-3 rounded text-sm space-y-1">
                            {Object.entries(formatReadableData(lead)).map(([key, value]) => (
                              <div key={key}>
                                <strong>{key}:</strong> {
                                  typeof value === 'object' && value !== null
                                    ? JSON.stringify(value, null, 2)
                                    : String(value)
                                }
                              </div>
                            ))}
                          </div>
                        </div>

                        {/* Raw JSON formát */}
                        <div>
                          <h4 className="font-medium mb-2">Raw Data</h4>
                          <pre className="bg-gray-50 p-3 rounded text-xs overflow-auto max-h-64">
                            {JSON.stringify(lead, null, 2)}
                          </pre>
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}

          {/* Spodní stránkování */}
          {!loading && leadHistory.length > 0 && (
            <div className="flex justify-center items-center space-x-2 mt-6">
              <Button
                variant="outline"
                size="sm"
                onClick={() => handlePageChange(1)}
                disabled={pagination.page === 1}
              >
                První
              </Button>
              
              <Button
                variant="outline"
                size="sm"
                onClick={() => handlePageChange(pagination.page - 1)}
                disabled={!pagination.hasPrev}
              >
                <ChevronLeft className="h-4 w-4" />
              </Button>

              {/* Zobrazení několika stránek kolem současné */}
              {Array.from({ length: Math.min(5, pagination.totalPages) }, (_, i) => {
                const pageNum = Math.max(1, pagination.page - 2) + i
                if (pageNum > pagination.totalPages) return null
                
                return (
                  <Button
                    key={pageNum}
                    variant={pageNum === pagination.page ? "default" : "outline"}
                    size="sm"
                    onClick={() => handlePageChange(pageNum)}
                  >
                    {pageNum}
                  </Button>
                )
              })}

              <Button
                variant="outline"
                size="sm"
                onClick={() => handlePageChange(pagination.page + 1)}
                disabled={!pagination.hasNext}
              >
                <ChevronRight className="h-4 w-4" />
              </Button>

              <Button
                variant="outline"
                size="sm"
                onClick={() => handlePageChange(pagination.totalPages)}
                disabled={pagination.page === pagination.totalPages}
              >
                Poslední
              </Button>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  )
}

export default MonitoringTab